import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChipsCellItem, ChipsCellOption } from './chips-cell.models';

@Component({
  selector: 'table-chips-cell',
  templateUrl: './chips-cell.component.html',
  styleUrls: ['./chips-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatChipsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatOptionModule,
    MatAutocompleteModule,
  ],
})
export class ChipsCellComponent<T = unknown, U = unknown> {
  items = input<ChipsCellItem<T>[]>([]);
  options = input<ChipsCellOption<U>[]>([]);
  showMore = input<boolean>(false);
  isCompactModeActivated = input<boolean>(false);
  canExpand = input<boolean>(true);
  canEdit = input<boolean>(true);
  showAddButton = input<boolean>(true);
  overflowTooltip = input<string>('');
  placeholder = input<string>('');
  ariaLabel = input<string>('');
  validators = input<ValidatorFn[]>([]);
  collapsedMaxLength = input<number>(12);
  expandedMaxLength = input<number>(Infinity);

  add = output<string>();
  selected = output<U>();
  remove = output<T>();
  toggleShowMore = output<boolean>();
  chipClick = output<T>();
  keyUp = output<string>();

  separatorKeysCodes: number[] = [ENTER, COMMA];

  chipsInput = viewChild<ElementRef<HTMLInputElement>>('chipsInput');
  autocompleteTrigger = viewChild(MatAutocompleteTrigger);

  ctrl = new FormControl('');
  inputSignal = toSignal(this.ctrl.valueChanges, { initialValue: '' });

  private validatorsEffect = effect(() => {
    this.ctrl.setValidators(this.validators());
    this.ctrl.updateValueAndValidity();
  });

  filteredOptions = computed(() =>
    this.inputSignal()
      ? this.filterByLabel(this.inputSignal(), this.options())
      : this.options().slice(),
  );

  addButtonDisabled = computed(() => {
    this.inputSignal();
    return !this.ctrl.value || this.ctrl.invalid;
  });

  displayItems = computed<ChipsCellItem<T>[]>(() => {
    const maxLen = this.showMore() ? this.expandedMaxLength() : this.collapsedMaxLength();
    return this.items().map((item) => ({
      ...item,
      label: this.truncate(item.label, maxLen),
    }));
  });

  onAdd(event: MatChipInputEvent) {
    const value = (event.value || '').trim();
    if (value) this.add.emit(value);
    event.chipInput?.clear();
    this.ctrl.setValue(null);
    this.clearInputElement();
  }

  onAddButton() {
    const value = (this.ctrl.value || '').trim();
    if (value) this.add.emit(value);
    this.ctrl.setValue(null);
    this.clearInputElement();
  }

  onSelected(event: MatAutocompleteSelectedEvent) {
    const option = this.options().find(({ value }) => value === event.option.value);
    if (option) this.selected.emit(option.data);
    this.ctrl.setValue(null);
    this.clearInputElement();
  }

  onRemove(item: ChipsCellItem<T>) {
    this.remove.emit(item.data);
  }

  onChipClick(item: ChipsCellItem<T>) {
    this.chipClick.emit(item.data);
  }

  onClickShowMore() {
    if (!this.showMore() && this.canExpand()) {
      this.toggleShowMore.emit(true);
      setTimeout(() => this.chipsInput()?.nativeElement?.focus(), 400);
    }
  }

  onClickShowLess(event: Event) {
    event.stopPropagation();
    if (this.showMore()) {
      this.toggleShowMore.emit(false);
      this.autocompleteTrigger()?.closePanel();
    }
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.code === 'Enter' || event.code === 'Space' || event.code === 'Comma') {
      const value = (this.ctrl.value || '').trim();
      if (value) this.keyUp.emit(value);
      this.ctrl.setValue('');
      this.clearInputElement();
      this.autocompleteTrigger()?.closePanel();
    }
  }

  private clearInputElement() {
    const el = this.chipsInput()?.nativeElement;
    if (el?.value !== undefined) {
      el.value = '';
    }
  }

  private filterByLabel(value: string, options: ChipsCellOption<U>[]): ChipsCellOption<U>[] {
    if (typeof value !== 'string') return options.slice();
    const filterValue = value.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(filterValue));
  }

  private truncate(text: string, maxLength: number): string {
    if (!text || !maxLength || maxLength <= 0 || text.length <= maxLength) return text ?? '';
    return `${text.slice(0, Math.max(0, maxLength - 3))}...`;
  }
}
